import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { storage } from '@/lib/storage';
import { isLandingAlreadyApplied, markLandingApplied } from '@/lib/landing';
import { useSettingsPatch, useUserPrefs } from '@/api/bi/settings';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/system/ErrorBoundary';
import { ResourceGate, useAccess } from '@/features/AccessProvider';
import { useAuth } from '@/auth/AuthProvider';
import { useCanUseLanding, useFirstAccessiblePath } from '@/features/nav';
import { notify } from '@/lib/toast';
import { sendTelemetry } from '@/lib/telemetry';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ForbiddenPage } from '@/pages/auth/ForbiddenPage';
import { NotFoundPage } from '@/pages/auth/NotFoundPage';
import { OverviewPage } from '@/pages/overview/OverviewPage';
import { LiveOpsPage } from '@/pages/live-ops/LiveOpsPage';
import { OrdersPage } from '@/pages/orders/OrdersPage';
import { GrowthPage } from '@/pages/growth/GrowthPage';
import { SupplyPage } from '@/pages/supply/SupplyPage';
import { InventoryLedgerPage } from '@/pages/inventory/InventoryLedgerPage';
import { ShrinkagePage } from '@/pages/inventory/ShrinkagePage';
import { StrategicPage } from '@/pages/strategic/StrategicPage';
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { SettingsLayout } from '@/pages/settings/SettingsLayout';
import { AccountPage } from '@/pages/settings/AccountPage';
import { AppearancePage } from '@/pages/settings/AppearancePage';
import { PreferencesPage } from '@/pages/settings/PreferencesPage';
import { SavedViewsPage } from '@/pages/settings/SavedViewsPage';
import AccessControlPage from '@/pages/settings/access/AccessControlPage';
import { OrderDetailPage } from '@/pages/orders/OrderDetailPage';
import { SlaHeatmapPage } from '@/pages/orders/SlaHeatmapPage';
import { DurationsPage } from '@/pages/orders/DurationsPage';
import { RidersDistributionPage } from '@/pages/riders/RidersDistributionPage';
import { CustomersPage } from '@/pages/customers/CustomersPage';
import { CustomerDetailPage } from '@/pages/customers/CustomerDetailPage';
import { StoreHealthPage } from '@/pages/stores/StoreHealthPage';
import { RidersPage } from '@/pages/riders/RidersPage';
import { SearchInsightsPage } from '@/pages/search/SearchInsightsPage';
import { WishlistPage } from '@/pages/wishlist/WishlistPage';
import { WaitlistPage } from '@/pages/waitlist/WaitlistPage';
import { VendorsPage } from '@/pages/vendors/VendorsPage';
import { ProfitabilityPage } from '@/pages/vendors/ProfitabilityPage';
import { ProcurementPage } from '@/pages/procurement/ProcurementPage';
import { ForecastPage } from '@/pages/inventory/ForecastPage';
import { ReplenishmentPage } from '@/pages/inventory/ReplenishmentPage';
import { ProductsPage } from '@/pages/products/ProductsPage';
import { BrandsPage } from '@/pages/products/BrandsPage';
import { CategoriesPage } from '@/pages/products/CategoriesPage';
import { ProductForecastPage } from '@/pages/products/ProductForecastPage';
import { ProductDetailPage } from '@/pages/products/ProductDetailPage';
import { RfmPage } from '@/pages/growth/RfmPage';
import { AbandonmentPage } from '@/pages/growth/AbandonmentPage';
import { CouponsPage } from '@/pages/growth/CouponsPage';
import { CouponsReferralsPage } from '@/pages/growth/CouponsReferralsPage';
import { ReferralsPage } from '@/pages/growth/ReferralsPage';
import { RosterPage } from '@/pages/roster/RosterPage';
import { PaymentsPage } from '@/pages/payments/PaymentsPage';
import { RefundsPage } from '@/pages/payments/RefundsPage';
import { StockAuditPage } from '@/pages/inventory/StockAuditPage';
import { LostItemsPage } from '@/pages/inventory/LostItemsPage';
import { TransfersPage } from '@/pages/inventory/TransfersPage';
import { FeedbackPage } from '@/pages/feedback/FeedbackPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { AlertsPage } from '@/pages/alerts/AlertsPage';
import { AuditPage } from '@/pages/audit/AuditPage';
import { WallPage } from '@/pages/wall/WallPage';
import { CrmInboxPage } from '@/pages/crm/CrmInboxPage';
import { CrmConversationsPage } from '@/pages/crm/CrmConversationsPage';
import { CrmConversationDetailPage } from '@/pages/crm/CrmConversationDetailPage';
import { CrmTicketsPage } from '@/pages/crm/CrmTicketsPage';
import { CrmTicketDetailPage } from '@/pages/crm/CrmTicketDetailPage';
import { CrmRefundsPage } from '@/pages/crm/CrmRefundsPage';
import { CrmShrinkagePage } from '@/pages/crm/CrmShrinkagePage';
import { CrmSettingsIndexPage } from '@/pages/crm/settings/CrmSettingsIndexPage';
import { CrmRoutingPage } from '@/pages/crm/settings/CrmRoutingPage';
import { CrmEmailSettingsPage } from '@/pages/crm/settings/CrmEmailSettingsPage';
import { CrmWhatsAppSettingsPage } from '@/pages/crm/settings/CrmWhatsAppSettingsPage';
import { CrmMetaSettingsPage } from '@/pages/crm/settings/CrmMetaSettingsPage';
import { CrmAiSettingsPage } from '@/pages/crm/settings/CrmAiSettingsPage';
import { CrmCannedRepliesPage } from '@/pages/crm/settings/CrmCannedRepliesPage';
import { CrmEngagementPage } from '@/pages/crm/settings/CrmEngagementPage';
import { CrmConversationAnalyticsPage } from '@/pages/crm/CrmConversationAnalyticsPage';
import { CrmTemplatesPage } from '@/pages/crm/settings/CrmTemplatesPage';
import { CrmCancellationsPage } from '@/pages/crm/settings/CrmCancellationsPage';
import { CrmSlaPolicyPage } from '@/pages/crm/settings/CrmSlaPolicyPage';
import { CrmReasonCodesPage } from '@/pages/crm/settings/CrmReasonCodesPage';
import { CrmSlaAnalyticsPage } from '@/pages/crm/CrmSlaAnalyticsPage';
import { UsersListPage } from '@/pages/users/UsersListPage';
import { UserManagementPage } from '@/pages/users/UserManagementPage';
import { PlaygroundPage } from '@/pages/playground/PlaygroundPage';

// Heavy 3D replay scene (three.js + GLB models) — code-split so the main
// bundle stays untouched.
const DeliveryReplayPage = lazy(() => import('@/pages/orders/DeliveryReplayPage'));

// Public location-share page (magic links sent to customers) — code-split so
// customers never download the dashboard bundle.
const LocateSharePage = lazy(() => import('@/pages/public/LocateSharePage'));

/*
 * Map-heavy screens. Each pulls in a mapping/3D stack that most sessions never
 * touch, so they load on demand behind the AppShell's page skeleton rather than
 * being paid for on every cold boot.
 */
const NetworkPage = lazy(() =>
  import('@/pages/network/NetworkPage').then((m) => ({ default: m.NetworkPage })),
);
const DarkstoreSitingPage = lazy(() =>
  import('@/pages/network/DarkstoreSitingPage').then((m) => ({ default: m.DarkstoreSitingPage })),
);
const ProductWardMapPage = lazy(() =>
  import('@/pages/products/ProductWardMapPage').then((m) => ({ default: m.ProductWardMapPage })),
);
const Building3DMapPage = lazy(() =>
  import('@/pages/products/Building3DMapPage').then((m) => ({ default: m.Building3DMapPage })),
);

function ReplayFallback() {
  return (
    <div className="fixed inset-0 z-[950] flex items-center justify-center bg-[#0b0d13]">
      <Loader2 className="h-8 w-8 animate-spin text-[#ee2a4c]" />
    </div>
  );
}

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * Index landing — honours the user's `landing_path` preference exactly
 * once per browser tab. Subsequent navigations to `/` stay on Overview
 * so the sidebar's Overview link works as expected.
 *
 * The "already applied" flag lives in sessionStorage (see `lib/landing.ts`)
 * because a per-mount `useRef` resets every time `<NavLink to="/">` re-mounts
 * this component, which made the redirect loop on every Overview click.
 */
function Landing({ children }: { children: ReactNode }) {
  const location = useLocation();
  const access = useAccess();
  // Prefer the live (server-backed) prefs so a freshly-arrived `landing_path`
  // is honoured on first paint instead of the stale local cache.
  const prefs = useUserPrefs();
  const canUseLanding = useCanUseLanding();
  const firstAccessible = useFirstAccessiblePath();
  const patchSettings = useSettingsPatch();

  const stored = prefs.landing_path || storage.getPrefs().landing_path || '/';
  const isDefault = stored === '/';
  // Until access resolves we know nothing, so assume the preference is fine
  // and let the render fall through to the gate's loader.
  const permitted = access.isReady ? canUseLanding(stored) : true;

  /*
   * Repair a preference the user cannot use any more.
   *
   * The old code only substituted a fallback when the target was `/`, so a
   * saved `landing_path` of, say, `/orders` was honoured even when `orders`
   * was denied — the user got redirected there and `ResourceGate` bounced them
   * to /forbidden. Because the "already applied" flag lives in sessionStorage,
   * that repeated in every new tab, which is what made it feel like a trap.
   *
   * We only rewrite an explicit choice; `/` is the default, not a decision, so
   * silently changing it would be surprising.
   */
  const healedRef = useRef(false);
  useEffect(() => {
    if (!access.isReady || healedRef.current) return;
    if (isDefault || permitted) return;
    healedRef.current = true;
    storage.setPrefs({ landing_path: firstAccessible ?? '/' });
    patchSettings({ prefs: storage.getPrefs() });
    notify.message('Your start page was reset — you no longer have access to the page it pointed at.');
  }, [access.isReady, isDefault, permitted, firstAccessible, patchSettings]);

  if (!access.isReady) return <>{children}</>;

  const target = permitted ? stored : (firstAccessible ?? '/');

  if (
    !isLandingAlreadyApplied() &&
    location.pathname === '/' &&
    location.search === '' &&
    target !== '/' &&
    target.startsWith('/')
  ) {
    markLandingApplied();
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

/**
 * Per-route wrapper:
 *
 *   - Scopes an ErrorBoundary to the page so a crash in `Orders` doesn't
 *     take down the whole shell (and the user can still navigate elsewhere).
 *   - Emits a `telemetry` mount ping with the route name. Useful for
 *     measuring the "blank → first paint" gap once we wire perf marks.
 */
function RoutePage({
  scope,
  slug,
  superuserOnly,
  children,
}: {
  scope: string;
  /** Optional feature-catalogue slug; when present the gate enforces can_view. */
  slug?: string;
  /**
   * For routes with no resource in the catalogue that still shouldn't be open
   * to everyone with a login. Grant-based gating is the norm; reach for this
   * only when there is deliberately no grant to hang the route off.
   */
  superuserOnly?: boolean;
  children: ReactNode;
}) {
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    void sendTelemetry({ kind: 'manual', message: 'route_mount', component: scope, extra: { page: scope } });
  }, [scope]);
  const inner = <ErrorBoundary scope={scope}>{children}</ErrorBoundary>;
  if (superuserOnly && !user?.is_superuser) {
    return <Navigate to="/forbidden" replace state={{ from: location.pathname }} />;
  }
  if (!slug) return inner;
  // Carry the attempted path so /forbidden can say *which* page was refused
  // and offer somewhere to go instead of just a sign-out button.
  return (
    <ResourceGate
      resource={slug}
      fallback={<Navigate to="/forbidden" replace state={{ from: location.pathname }} />}
    >
      {inner}
    </ResourceGate>
  );
}

/** `/users/:id` → `/settings/users/:id`, keeping the id and any query. */
function RedirectUserDetail() {
  const { id } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/settings/users/${id ?? ''}${search}${hash}`} replace />;
}

/** `/crm/settings/<rest>` → `/settings/support/<rest>`. */
function RedirectCrmSettings() {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/crm\/settings\/?/, '');
  return <Navigate to={`/settings/support${rest ? `/${rest}` : ''}${search}${hash}`} replace />;
}

// Dev-only 3D replay + darkstore harnesses (synthetic data, no auth) —
// tree-shaken from production builds by the `import.meta.env.DEV` guard below.
const ReplayDevHarness = lazy(() => import('@/pages/orders/ReplayDevHarness'));
const DarkstoreDevHarness = lazy(() => import('@/pages/products/DarkstoreDevHarness'));

export const router = createBrowserRouter([
  ...(import.meta.env.DEV
    ? [
        {
          path: '/__replay-dev',
          element: (
            <Suspense fallback={<ReplayFallback />}>
              <ReplayDevHarness />
            </Suspense>
          ),
        },
        {
          path: '/__darkstore-dev',
          element: (
            <Suspense fallback={<ReplayFallback />}>
              <DarkstoreDevHarness />
            </Suspense>
          ),
        },
      ]
    : []),
  { path: '/login', element: <LoginPage /> },
  { path: '/forbidden', element: <ForbiddenPage /> },
  // Public (unauthenticated) customer-facing page: one-time location share
  // links sent by support on IG / Messenger / email. Code-split — customers
  // should not download the BI bundle.
  {
    path: '/locate/:token',
    element: (
      <Suspense fallback={null}>
        <LocateSharePage />
      </Suspense>
    ),
  },
  {
    path: '/wall',
    element: (
      <ProtectedRoute>
        <RoutePage scope="wall" slug="wall"><WallPage /></RoutePage>
      </ProtectedRoute>
    ),
  },
  {
    element: (
      <ProtectedRoute>
        <ShellLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Landing><RoutePage scope="overview" slug="overview"><OverviewPage /></RoutePage></Landing> },
      { path: 'live', element: <RoutePage scope="live-ops" slug="live-ops"><LiveOpsPage /></RoutePage> },
      { path: 'orders', element: <RoutePage scope="orders" slug="orders"><OrdersPage /></RoutePage> },
      { path: 'orders/sla-heatmap', element: <RoutePage scope="orders-sla-heatmap" slug="orders.sla-heatmap"><SlaHeatmapPage /></RoutePage> },
      { path: 'orders/durations', element: <RoutePage scope="orders-durations" slug="orders.durations"><DurationsPage /></RoutePage> },
      { path: 'orders/:id', element: <RoutePage scope="order-detail" slug="orders"><OrderDetailPage /></RoutePage> },
      {
        path: 'orders/:id/replay',
        element: (
          <RoutePage scope="order-replay" slug="orders">
            <Suspense fallback={<ReplayFallback />}>
              <DeliveryReplayPage />
            </Suspense>
          </RoutePage>
        ),
      },
      { path: 'riders/distribution', element: <RoutePage scope="riders-distribution" slug="riders.distribution"><RidersDistributionPage /></RoutePage> },
      { path: 'customers', element: <RoutePage scope="customers" slug="customers"><CustomersPage /></RoutePage> },
      { path: 'customers/:id', element: <RoutePage scope="customer-detail" slug="customers"><CustomerDetailPage /></RoutePage> },
      { path: 'stores', element: <RoutePage scope="stores" slug="stores"><StoreHealthPage /></RoutePage> },
      { path: 'riders', element: <RoutePage scope="riders" slug="riders"><RidersPage /></RoutePage> },
      { path: 'search', element: <RoutePage scope="search" slug="search"><SearchInsightsPage /></RoutePage> },
      { path: 'wishlist', element: <RoutePage scope="wishlist" slug="wishlist"><WishlistPage /></RoutePage> },
      { path: 'waitlist', element: <RoutePage scope="waitlist" slug="waitlist"><WaitlistPage /></RoutePage> },
      { path: 'network', element: <RoutePage scope="network" slug="network"><NetworkPage /></RoutePage> },
      { path: 'network/darkstore-siting', element: <RoutePage scope="network-darkstore-siting" slug="network.darkstore-siting"><DarkstoreSitingPage /></RoutePage> },
      { path: 'vendors', element: <RoutePage scope="vendors" slug="vendors"><VendorsPage /></RoutePage> },
      { path: 'vendors/profitability', element: <RoutePage scope="vendors-profitability" slug="vendors.profitability"><ProfitabilityPage /></RoutePage> },
      { path: 'procurement', element: <RoutePage scope="procurement" slug="procurement"><ProcurementPage /></RoutePage> },
      { path: 'products', element: <RoutePage scope="products" slug="products"><ProductsPage /></RoutePage> },
      { path: 'products/brands', element: <RoutePage scope="products-brands" slug="products.brands"><BrandsPage /></RoutePage> },
      { path: 'products/categories', element: <RoutePage scope="products-categories" slug="products.categories"><CategoriesPage /></RoutePage> },
      { path: 'products/forecast', element: <RoutePage scope="products-forecast" slug="products.forecast"><ProductForecastPage /></RoutePage> },
      { path: 'products/map', element: <RoutePage scope="products-map" slug="products.map"><ProductWardMapPage /></RoutePage> },
      { path: 'products/map3d', element: <RoutePage scope="products-map3d" slug="products.map3d"><Building3DMapPage /></RoutePage> },
      { path: 'products/:id', element: <RoutePage scope="product-detail" slug="products"><ProductDetailPage /></RoutePage> },
      { path: 'inventory/forecast', element: <RoutePage scope="forecast" slug="inventory.forecast"><ForecastPage /></RoutePage> },
      { path: 'inventory/replenishment', element: <RoutePage scope="replenishment" slug="inventory.replenishment"><ReplenishmentPage /></RoutePage> },
      { path: 'growth', element: <RoutePage scope="growth" slug="growth"><GrowthPage /></RoutePage> },
      { path: 'growth/rfm', element: <RoutePage scope="growth-rfm" slug="growth.rfm"><RfmPage /></RoutePage> },
      { path: 'growth/abandonment', element: <RoutePage scope="growth-abandonment" slug="growth.abandonment"><AbandonmentPage /></RoutePage> },
      { path: 'growth/coupons-referrals', element: <RoutePage scope="growth-coupons-referrals" slug="growth.coupons-referrals"><CouponsReferralsPage /></RoutePage> },
      { path: 'growth/coupons', element: <RoutePage scope="growth-coupons" slug="growth.coupons"><CouponsPage /></RoutePage> },
      { path: 'growth/referrals', element: <RoutePage scope="growth-referrals" slug="growth.referrals"><ReferralsPage /></RoutePage> },
      { path: 'supply', element: <RoutePage scope="supply" slug="supply"><SupplyPage /></RoutePage> },
      { path: 'inventory', element: <RoutePage scope="inventory" slug="inventory"><InventoryLedgerPage /></RoutePage> },
      { path: 'inventory/audits', element: <RoutePage scope="inventory-audits" slug="inventory.audits"><StockAuditPage /></RoutePage> },
      { path: 'inventory/transfers', element: <RoutePage scope="inventory-transfers" slug="inventory.transfers"><TransfersPage /></RoutePage> },
      { path: 'inventory/lost-items', element: <RoutePage scope="inventory-lost-items" slug="inventory.lost-items"><LostItemsPage /></RoutePage> },
      { path: 'shrinkage', element: <RoutePage scope="shrinkage" slug="shrinkage"><ShrinkagePage /></RoutePage> },
      { path: 'strategic', element: <RoutePage scope="strategic" slug="strategic"><StrategicPage /></RoutePage> },
      { path: 'reports', element: <RoutePage scope="reports" slug="reports"><ReportsPage /></RoutePage> },
      { path: 'roster', element: <RoutePage scope="roster" slug="roster"><RosterPage /></RoutePage> },
      { path: 'payments', element: <RoutePage scope="payments" slug="payments"><PaymentsPage /></RoutePage> },
      { path: 'payments/refunds', element: <RoutePage scope="payments-refunds" slug="payments.refunds"><RefundsPage /></RoutePage> },
      { path: 'feedback', element: <RoutePage scope="feedback" slug="feedback"><FeedbackPage /></RoutePage> },
      { path: 'notifications', element: <RoutePage scope="notifications" slug="notifications"><NotificationsPage /></RoutePage> },
      { path: 'alerts', element: <RoutePage scope="alerts" slug="alerts"><AlertsPage /></RoutePage> },

      /*
       * Settings hub. One layout, one sub-nav, four sections: personal
       * account, workspace defaults, administration and Support CRM config.
       * Everything below used to be scattered across `/settings`, `/users`,
       * `/audit` and `/crm/settings/*` with no relationship between them.
       */
      {
        path: 'settings',
        element: <RoutePage scope="settings" slug="settings"><SettingsLayout /></RoutePage>,
        children: [
          { index: true, element: <Navigate to="/settings/account" replace /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'appearance', element: <AppearancePage /> },
          { path: 'preferences', element: <PreferencesPage /> },
          { path: 'views', element: <SavedViewsPage /> },
          { path: 'access', element: <AccessControlPage /> },
          { path: 'audit', element: <RoutePage scope="audit" slug="audit"><AuditPage /></RoutePage> },
          { path: 'users', element: <RoutePage scope="user-management" slug="settings.user-management"><UsersListPage /></RoutePage> },
          { path: 'users/:id', element: <RoutePage scope="user-management-detail" slug="settings.user-management"><UserManagementPage /></RoutePage> },
          { path: 'support', element: <RoutePage scope="crm-settings" slug="crm.settings"><CrmSettingsIndexPage /></RoutePage> },
          { path: 'support/routing', element: <RoutePage scope="crm-settings-routing" slug="crm.settings.routing"><CrmRoutingPage /></RoutePage> },
          { path: 'support/email', element: <RoutePage scope="crm-settings-email" slug="crm.settings.email"><CrmEmailSettingsPage /></RoutePage> },
          { path: 'support/whatsapp', element: <RoutePage scope="crm-settings-whatsapp" slug="crm.settings.whatsapp"><CrmWhatsAppSettingsPage /></RoutePage> },
          { path: 'support/meta', element: <RoutePage scope="crm-settings-meta" slug="crm.settings.meta"><CrmMetaSettingsPage /></RoutePage> },
          { path: 'support/ai', element: <RoutePage scope="crm-settings-ai" slug="crm.settings.ai"><CrmAiSettingsPage /></RoutePage> },
          { path: 'support/templates', element: <RoutePage scope="crm-settings-templates" slug="crm.settings.templates"><CrmTemplatesPage /></RoutePage> },
          { path: 'support/cancellations', element: <RoutePage scope="crm-settings-cancellations" slug="crm.settings.cancellations"><CrmCancellationsPage /></RoutePage> },
          { path: 'support/sla', element: <RoutePage scope="crm-settings-sla" slug="crm.settings.sla"><CrmSlaPolicyPage /></RoutePage> },
          {
            path: 'support/reason-codes',
            element: (
              <RoutePage scope="crm-settings-reason-codes" slug="crm.settings.reason_codes">
                <CrmReasonCodesPage />
              </RoutePage>
            ),
          },
          { path: 'support/canned-replies', element: <RoutePage scope="crm-settings-canned-replies" slug="crm.settings.canned_replies"><CrmCannedRepliesPage /></RoutePage> },
          { path: 'support/engagement', element: <RoutePage scope="crm-settings-engagement" slug="crm.settings.engagement"><CrmEngagementPage /></RoutePage> },
        ],
      },

      // Redirects for every URL the consolidation moved. People have these
      // bookmarked and pasted into tickets; they must keep working.
      { path: 'users', element: <Navigate to="/settings/users" replace /> },
      { path: 'users/:id', element: <RedirectUserDetail /> },
      { path: 'audit', element: <Navigate to="/settings/audit" replace /> },
      { path: 'crm/settings', element: <Navigate to="/settings/support" replace /> },
      { path: 'crm/settings/*', element: <RedirectCrmSettings /> },

      // TEMPORARY: data downloads for the travel-time/traffic ETA project.
      // Not in the sidebar, and no catalogue resource to gate it on, so it is
      // restricted to superusers rather than to anyone holding a login — it
      // exports raw rider movement data. Remove with pages/playground/.
      {
        path: 'playground',
        element: (
          <RoutePage scope="playground" superuserOnly>
            <PlaygroundPage />
          </RoutePage>
        ),
      },
      { path: 'crm', element: <Navigate to="/crm/inbox" replace /> },
      { path: 'crm/inbox', element: <RoutePage scope="crm-inbox" slug="crm.inbox"><CrmInboxPage /></RoutePage> },
      { path: 'crm/conversations', element: <RoutePage scope="crm-conversations" slug="crm.conversations"><CrmConversationsPage /></RoutePage> },
      { path: 'crm/conversations/:id', element: <RoutePage scope="crm-conversation-detail" slug="crm.conversations"><CrmConversationDetailPage /></RoutePage> },
      { path: 'crm/tickets', element: <RoutePage scope="crm-tickets" slug="crm.tickets"><CrmTicketsPage /></RoutePage> },
      { path: 'crm/tickets/:id', element: <RoutePage scope="crm-ticket-detail" slug="crm.tickets"><CrmTicketDetailPage /></RoutePage> },
      { path: 'crm/refunds', element: <RoutePage scope="crm-refunds" slug="crm.refunds"><CrmRefundsPage /></RoutePage> },
      { path: 'crm/shrinkage', element: <RoutePage scope="crm-shrinkage" slug="crm.shrinkage"><CrmShrinkagePage /></RoutePage> },
      { path: 'crm/analytics/sla', element: <RoutePage scope="crm-analytics-sla" slug="crm.analytics.sla"><CrmSlaAnalyticsPage /></RoutePage> },
      { path: 'crm/analytics/conversations', element: <RoutePage scope="crm-analytics-conversations" slug="crm.analytics.conversations"><CrmConversationAnalyticsPage /></RoutePage> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
